import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { api, downloadFile } from '@/lib/api/client'
import { isApiError } from '@/lib/api/errors'
import { asRows } from '@/lib/api/list'
import { useAuth } from '@/lib/auth/AuthProvider'
import {
  HR_ALLOWANCE_READ_PERM,
  HR_COMPENSATION_READ_PERM,
  HR_DOC_CASE_READ_PERM,
  HR_DOC_MISSING_READ_PERM,
  HR_DOCUMENTS_READ_PERM,
  HR_LEAVE_READ_PERM,
  HR_PAYMENT_READ_PERM,
  HR_PAYROLL_READ_PERM,
  HR_READ_PERM,
  HR_SCHEDULE_READ_PERM,
} from '../permissions'
import {
  bordroApiStatus,
  buildDetail,
  employeeBody,
  mailboxPayload,
  mapPayment,
  mapPersonel,
  mapProfile,
  money,
  payrollRows,
  rec,
  scheduleBody,
  str,
  workStatusOf,
  type DetailSources,
  type OdemeView,
} from './map'
import type { PersonelDetail, PersonelProfile } from './model'
import type { PersonelFormValues } from './schemas/personelFormSchema'
import type { BordroDurum, BordroSatir, DetailTab, Personel, PersonelDurum } from './types'

export const hrKeys = {
  all: ['hr'] as const,
  /** Aktif personel dizini (`list-all`) — Erişim modülündeki seçiciler de aynı anahtarı kullanır. */
  employees: ['hr', 'employees'] as const,
  employee: (id: string) => ['hr', 'employees', id] as const,
  section: (id: string, section: string) => ['hr', 'employees', id, section] as const,
  payroll: (period: string) => ['hr', 'payroll', period] as const,
  payments: ['hr', 'payments'] as const,
  payrollParams: ['hr', 'payroll-params'] as const,
}

/** Personel dizini ham satırları; ekranlar `select` ile kendi modeline çevirir. */
export const fetchEmployeeDirectory = async () => asRows(await api.get<unknown>('/api/v1/hr/employees/list-all'))

export function usePersonelQuery() {
  return useQuery({ queryKey: hrKeys.employees, queryFn: fetchEmployeeDirectory, staleTime: 60_000, select: (rows) => rows.map(mapPersonel) })
}

export function usePersonelList(): Personel[] {
  return usePersonelQuery().data ?? EMPTY
}
const EMPTY: Personel[] = []

export function useManager(managerId: string) {
  const list = usePersonelList()
  return useMemo(() => list.find((item) => item.id === managerId), [list, managerId])
}

// --- Detay: temel kart + sekme bazlı, yetkiye bağlı bölümler ---

type Section = keyof DetailSources | 'schedule'

const SECTION_LOADERS: Record<Section, (id: string) => Promise<unknown>> = {
  schedule: (id) => api.get(`/api/v1/hr/employees/${id}/work-schedule`),
  compensations: (id) => api.get(`/api/v1/hr/employees/${id}/compensations`),
  leaves: (id) => api.get('/api/v1/hr/leaves', { employee_id: id }),
  payrolls: (id) => api.get(`/api/v1/hr/payroll/employees/${id}/payrolls`),
  payments: (id) => api.get(`/api/v1/hr/payroll/employees/${id}/payments`),
  allowances: (id) => api.get(`/api/v1/hr/payroll/employees/${id}/allowances`),
  personnelFile: (id) => api.get(`/api/v1/hr/employees/${id}/documents`),
  cases: (id) => api.get('/api/v1/documents/cases', { domain: 'employee', employee_id: id, limit: 100 }),
  missing: (id) => api.get('/api/v1/documents/missing', { domain: 'employee', employee_id: id }),
  performance: (id) => api.get(`/api/v1/hr/employees/${id}/performance`),
  history: async () => null,
}

const SECTION_PERMS: Record<Section, string> = {
  schedule: HR_SCHEDULE_READ_PERM,
  compensations: HR_COMPENSATION_READ_PERM,
  leaves: HR_LEAVE_READ_PERM,
  payrolls: HR_PAYROLL_READ_PERM,
  payments: HR_PAYMENT_READ_PERM,
  allowances: HR_ALLOWANCE_READ_PERM,
  personnelFile: HR_DOCUMENTS_READ_PERM,
  cases: HR_DOC_CASE_READ_PERM,
  missing: HR_DOC_MISSING_READ_PERM,
  performance: HR_READ_PERM,
  history: HR_READ_PERM,
}

/** Hangi ekran hangi bölümleri ister. Sekme açılmadan o sekmenin verisi çekilmez. */
const VIEW_SECTIONS: Record<DetailTab | 'form', Section[]> = {
  form: ['schedule', 'compensations'],
  genel: ['schedule', 'compensations', 'personnelFile'],
  ucret: ['compensations', 'payrolls', 'allowances'],
  izin: ['leaves'],
  belge: ['personnelFile', 'cases', 'missing'],
  odeme: ['payments'],
  perf: ['performance'],
}

function useSection(id: string, section: Section, enabled: boolean) {
  return useQuery({
    queryKey: hrKeys.section(id, section),
    queryFn: () => SECTION_LOADERS[section](id),
    enabled: enabled && !!id,
    staleTime: 30_000,
  })
}

export type PersonelView = {
  isLoading: boolean
  isError: boolean
  sectionLoading: boolean
  personel?: Personel
  profile?: PersonelProfile
  detail?: PersonelDetail
}

export function usePersonelView(id: string, view: DetailTab | 'form'): PersonelView {
  const { can } = useAuth()
  const base = useQuery({
    queryKey: hrKeys.employee(id),
    // sensitive=true: yetki varsa TCKN açık + IBAN maskeli döner; yoksa BE sessizce normal okumaya düşer.
    queryFn: () => api.get<unknown>(`/api/v1/hr/employees/${id}`, { sensitive: true }),
    enabled: !!id,
  })
  const wanted = new Set(VIEW_SECTIONS[view])
  const on = (s: Section) => wanted.has(s) && can(SECTION_PERMS[s])
  const sections = {
    schedule: useSection(id, 'schedule', on('schedule')),
    compensations: useSection(id, 'compensations', on('compensations')),
    leaves: useSection(id, 'leaves', on('leaves')),
    payrolls: useSection(id, 'payrolls', on('payrolls')),
    payments: useSection(id, 'payments', on('payments')),
    allowances: useSection(id, 'allowances', on('allowances')),
    personnelFile: useSection(id, 'personnelFile', on('personnelFile')),
    cases: useSection(id, 'cases', on('cases')),
    missing: useSection(id, 'missing', on('missing')),
    performance: useSection(id, 'performance', on('performance')),
  }
  const raw = base.data
  const schedule = sections.schedule.data
  const compensations = sections.compensations.data
  const leaves = sections.leaves.data
  const payrolls = sections.payrolls.data
  const payments = sections.payments.data
  const allowances = sections.allowances.data
  const personnelFile = sections.personnelFile.data
  const cases = sections.cases.data
  const missing = sections.missing.data
  const performance = sections.performance.data
  const built = useMemo(() => {
    if (!raw) return {}
    const personel = mapPersonel(raw)
    const profile = mapProfile(raw, personel, { schedule, compensations })
    const detail = buildDetail(personel, profile, {
      history: rec(raw).assignment_history,
      leaves,
      compensations,
      payrolls,
      payments,
      allowances,
      personnelFile,
      cases,
      missing,
      performance,
    })
    return { personel, profile, detail }
  }, [raw, schedule, compensations, leaves, payrolls, payments, allowances, personnelFile, cases, missing, performance])
  return {
    ...built,
    isLoading: base.isLoading,
    isError: base.isError,
    sectionLoading: Object.values(sections).some((q) => q.isLoading),
  }
}

/** Form ekranı: temel kart + mesai takvimi + ücret. */
export const useEmployeeDetail = (id: string) => usePersonelView(id, 'form')

// --- Önbellek: personel bazında dar invalidasyon ---

function invalidateEmployee(qc: QueryClient, id?: string) {
  void qc.invalidateQueries({ queryKey: hrKeys.employees, exact: true })
  if (id) void qc.invalidateQueries({ queryKey: hrKeys.employee(id) })
}

const invalidateSection = (qc: QueryClient, id: string, ...sections: Section[]) =>
  sections.forEach((s) => void qc.invalidateQueries({ queryKey: hrKeys.section(id, s) }))

// --- Kayıt ---

type SaveInput = { id?: string; values: PersonelFormValues; current?: PersonelProfile; photo?: File | null }

export function useSavePersonel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, values, current, photo }: SaveInput) => {
      const body = employeeBody(values)
      let employeeId = id ?? ''
      let userId: string
      let hadAccess = Boolean(current?.panel_erisim)
      if (id) {
        const saved = rec(await api.put(`/api/v1/hr/employees/${id}`, body))
        userId = str(saved.user_id)
        hadAccess = hadAccess || Boolean(userId)
      } else {
        const roleIds = (values.role_ids ?? []).filter(Boolean)
        const created = rec(
          await api.post('/api/v1/hr/employees', {
            ...body,
            create_system_access: values.panel_erisim,
            role_ids: values.panel_erisim ? roleIds : [],
          }),
        )
        employeeId = str(created.id)
        userId = str(created.user_id)
      }

      // Mesai saatleri: yalnız değiştiyse (ya da yeni kayıtta) takvim güncellenir.
      const scheduleChanged = !current || current.calisma_baslangic !== values.calisma_baslangic || current.calisma_bitis !== values.calisma_bitis || !current.takvim.length
      if (scheduleChanged) {
        await api.put(`/api/v1/hr/employees/${employeeId}/work-schedule`, scheduleBody(current?.takvim ?? [], values.calisma_baslangic, values.calisma_bitis))
      }

      // Ücret: yalnız tutar değiştiyse yeni ücret dönemi açılır (maaş geçmişi her kayıtta kirlenmez).
      if (values.brut_maas && money(values.brut_maas) !== (current?.brut_kayitli ?? '')) {
        await api.post(`/api/v1/hr/employees/${employeeId}/compensations`, {
          gross_amount: money(values.brut_maas),
          currency_code: 'TRY',
          pay_period: 'MONTHLY',
          valid_from: values.maas_gecerlilik || (id ? new Date().toISOString().slice(0, 10) : values.ise_giris),
          change_reason: id ? 'Ücret güncellemesi' : 'İşe giriş ücreti',
        })
      }

      // Panel erişimi + iş maili kutusu.
      const mailbox = mailboxPayload(values)
      if (values.panel_erisim && id && !hadAccess) {
        await api.post(`/api/v1/hr/employees/${employeeId}/system-access`, {
          work_email: values.firma_email,
          role_ids: (values.role_ids ?? []).filter(Boolean),
          ...(mailbox ? { mailbox } : {}),
        })
      } else if (values.panel_erisim && mailbox && userId) {
        await api.post('/api/v1/mail/accounts', { user_id: userId, email: values.firma_email, ...mailbox })
      }
      if (id && values.sifre_sifirla && userId) await api.post(`/api/v1/users/${userId}/reset-password`, {})

      // Özlük dosyası: formda seçilen belgeler kayıttan sonra şifreli depoya yüklenir.
      for (const doc of values.belgeler ?? []) {
        if (!doc.dosya) continue
        const form = new FormData()
        form.append('kind', doc.tip)
        form.append('file', doc.dosya)
        await api.post(`/api/v1/hr/employees/${employeeId}/documents`, form)
      }

      if (photo) {
        const form = new FormData()
        form.append('file', photo)
        await api.post(`/api/v1/hr/employees/${employeeId}/photo`, form)
      }
      return employeeId
    },
    onSuccess: (employeeId, vars) => {
      invalidateEmployee(qc, employeeId)
      if (vars.values.belgeler?.some((d) => d.dosya)) invalidateSection(qc, employeeId, 'personnelFile')
      if (vars.values.panel_erisim) void qc.invalidateQueries({ queryKey: ['access', 'users'] })
    },
  })
}

export function useChangeWorkStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, durum }: { id: string; durum: PersonelDurum }) => api.patch(`/api/v1/hr/employees/${id}/status`, { status: workStatusOf(durum) }),
    onSuccess: (_d, { id }) => invalidateEmployee(qc, id),
  })
}

export function useArchiveEmployee() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.del(`/api/v1/hr/employees/${id}`),
    onSuccess: (_d, id) => {
      invalidateEmployee(qc)
      qc.removeQueries({ queryKey: hrKeys.employee(id) })
    },
  })
}

export function useTerminateEmployee() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => api.post(`/api/v1/hr/employees/${id}/terminate`, { reason: reason ?? '' }),
    onSuccess: (_d, { id }) => invalidateEmployee(qc, id),
  })
}

export function useUploadEmployeePhoto() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => {
      const body = new FormData()
      body.append('file', file)
      return api.post(`/api/v1/hr/employees/${id}/photo`, body)
    },
    onSuccess: (_d, { id }) => invalidateEmployee(qc, id),
  })
}

export function useDocumentKinds() {
  return useQuery({
    queryKey: ['hr', 'document-kinds'],
    staleTime: 10 * 60_000,
    queryFn: async () =>
      asRows(await api.get<unknown>('/api/v1/hr/document-kinds')).map((row) => ({
        key: str(row.key),
        title: str(row.title),
        required: row.required_on_hire === true,
      })),
  })
}

/** Özlük dosyası (yüklenen belgeler + eksik zorunlu türler); detaydaki bölüm önbelleğiyle aynı anahtar. */
export function usePersonnelFile(employeeId: string, enabled = true) {
  const { can } = useAuth()
  return useSection(employeeId, 'personnelFile', enabled && can(HR_DOCUMENTS_READ_PERM))
}

export function useUploadPersonnelDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      employeeId,
      kind,
      file,
      title,
      validUntil,
      documentDate,
    }: {
      employeeId: string
      kind: string
      file: File
      title?: string
      validUntil?: string
      documentDate?: string
    }) => {
      const form = new FormData()
      form.append('kind', kind)
      form.append('file', file)
      if (title?.trim()) form.append('title', title.trim())
      if (validUntil) form.append('valid_until', validUntil)
      if (documentDate) form.append('document_date', documentDate)
      return api.post(`/api/v1/hr/employees/${employeeId}/documents`, form)
    },
    onSuccess: (_d, { employeeId }) => invalidateSection(qc, employeeId, 'personnelFile'),
  })
}

export type HistoricalPayrollInput = {
  employeeId: string
  period: string
  gross: string
  net: string
  sgk?: string
  incomeTax?: string
  stampTax?: string
  bonus?: string
  workedDays?: string
  note?: string
  file?: File | null
}

/** Sistem öncesi döneme ait, zaten ödenmiş bordroyu (isteğe bağlı muhasebe belgesiyle) ekler; onay/ödeme üretmez. */
export function useCreateHistoricalPayroll() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: HistoricalPayrollInput) => {
      const form = new FormData()
      const add = (k: string, v?: string) => v?.trim() && form.append(k, v.trim())
      add('period', input.period)
      add('gross_salary', input.gross)
      add('net_salary', input.net)
      add('sgk_deduction', input.sgk)
      add('income_tax', input.incomeTax)
      add('stamp_tax', input.stampTax)
      add('bonus', input.bonus)
      add('worked_days', input.workedDays)
      add('note', input.note)
      if (input.file) form.append('file', input.file)
      return api.post(`/api/v1/hr/payroll/employees/${input.employeeId}/payrolls/historical`, form)
    },
    onSuccess: (_d, { employeeId }) => invalidateSection(qc, employeeId, 'payrolls'),
  })
}

export function useDeleteHistoricalPayroll() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ payrollId }: { employeeId: string; payrollId: string }) => api.del(`/api/v1/hr/payroll/payrolls/${payrollId}/historical`),
    onSuccess: (_d, { employeeId }) => invalidateSection(qc, employeeId, 'payrolls'),
  })
}

/** Personel ödemesi türleri (sunucu) → geçmiş ödeme formu. */
export const HISTORICAL_PAYMENT_TYPES = ['SALARY', 'ADVANCE', 'MEAL', 'TRANSPORT', 'BONUS', 'EXPENSE'] as const
export type HistoricalPaymentInput = {
  employeeId: string
  paymentType: (typeof HISTORICAL_PAYMENT_TYPES)[number]
  amount: string
  period: string
  paymentDate: string
  description: string
}

export function useCreateHistoricalPayment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: HistoricalPaymentInput) =>
      api.post(`/api/v1/hr/payroll/employees/${input.employeeId}/payments/historical`, {
        payment_type: input.paymentType,
        amount: input.amount,
        period: input.period,
        payment_date: input.paymentDate,
        description: input.description.trim() || undefined,
      }),
    onSuccess: (_d, { employeeId }) => {
      void qc.invalidateQueries({ queryKey: hrKeys.payments })
      return invalidateSection(qc, employeeId, 'payments')
    },
  })
}

export function useDeleteHistoricalPayment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ paymentId }: { employeeId: string; paymentId: string }) => api.del(`/api/v1/hr/payroll/payments/${paymentId}/historical`),
    onSuccess: (_d, { employeeId }) => {
      void qc.invalidateQueries({ queryKey: hrKeys.payments })
      return invalidateSection(qc, employeeId, 'payments')
    },
  })
}

/** Özlük belgesini arşive alır (kayıt ve dosya saklanır, listeden çıkar). */
export function useArchivePersonnelDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ employeeId, documentId }: { employeeId: string; documentId: string }) => api.del(`/api/v1/hr/employees/${employeeId}/documents/${documentId}`),
    onSuccess: (_d, { employeeId }) => invalidateSection(qc, employeeId, 'personnelFile'),
  })
}

export const downloadPersonnelDocument = (employeeId: string, documentId: string, name: string) =>
  downloadFile(`/api/v1/hr/employees/${employeeId}/documents/${documentId}/download`, name)

// --- İzin ---

export function useCreateLeave() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { employeeId: string; leaveTypeId: string; start: string; end: string; days: number; reason?: string }) =>
      api.post('/api/v1/hr/leaves', {
        employee_id: input.employeeId,
        leave_type_id: input.leaveTypeId,
        start_date: input.start,
        end_date: input.end,
        total_days: input.days,
        reason: input.reason ?? '',
      }),
    onSuccess: (_d, { employeeId }) => invalidateSection(qc, employeeId, 'leaves'),
  })
}

export function useDecideLeave() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, approve, reason }: { id: string; employeeId: string; approve: boolean; reason?: string }) =>
      api.post(`/api/v1/hr/leaves/${id}/${approve ? 'approve' : 'reject'}`, { note: reason ?? '' }),
    onSuccess: (_d, { employeeId }) => invalidateSection(qc, employeeId, 'leaves'),
  })
}

// --- Performans (yalnız performans bölümü yenilenir) ---

function usePerfMutation<A extends { employeeId: string }>(fn: (arg: A) => Promise<unknown>) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: fn, onSuccess: (_d, { employeeId }) => invalidateSection(qc, employeeId, 'performance') })
}

export const useCreateReview = () =>
  usePerfMutation(({ employeeId, score, body }: { employeeId: string; score: number; body: string }) => api.post(`/api/v1/hr/employees/${employeeId}/reviews`, { score, body }))
export const useCreateGoal = () =>
  usePerfMutation((input: { employeeId: string; name: string; description?: string; progress?: number; status?: string; dueDate?: string }) =>
    api.post(`/api/v1/hr/employees/${input.employeeId}/goals`, {
      name: input.name,
      description: input.description ?? '',
      progress: input.progress ?? 0,
      status: input.status ?? 'on_track',
      due_date: input.dueDate,
    }),
  )
export const useUpdateGoal = () =>
  usePerfMutation(({ employeeId, goalId, body }: { employeeId: string; goalId: string; body: Record<string, unknown> }) => api.put(`/api/v1/hr/employees/${employeeId}/goals/${goalId}`, body))
export const useDeleteGoal = () =>
  usePerfMutation(({ employeeId, goalId }: { employeeId: string; goalId: string }) => api.del(`/api/v1/hr/employees/${employeeId}/goals/${goalId}`))
export const useCreateSkill = () =>
  usePerfMutation(({ employeeId, name, score }: { employeeId: string; name: string; score: number }) => api.post(`/api/v1/hr/employees/${employeeId}/skills`, { name, score }))
export const useUpdateSkill = () =>
  usePerfMutation(({ employeeId, skillId, body }: { employeeId: string; skillId: string; body: Record<string, unknown> }) => api.put(`/api/v1/hr/employees/${employeeId}/skills/${skillId}`, body))
export const useDeleteSkill = () =>
  usePerfMutation(({ employeeId, skillId }: { employeeId: string; skillId: string }) => api.del(`/api/v1/hr/employees/${employeeId}/skills/${skillId}`))
export const useCreateTraining = () =>
  usePerfMutation((input: { employeeId: string; name: string; provider?: string; date?: string }) =>
    api.post(`/api/v1/hr/employees/${input.employeeId}/trainings`, { title: input.name, provider: input.provider ?? '', training_date: input.date, status: 'planned' }),
  )
export const useUpdateTraining = () =>
  usePerfMutation(({ employeeId, trainingId, body }: { employeeId: string; trainingId: string; body: Record<string, unknown> }) =>
    api.put(`/api/v1/hr/employees/${employeeId}/trainings/${trainingId}`, body),
  )
export const useDeleteTraining = () =>
  usePerfMutation(({ employeeId, trainingId }: { employeeId: string; trainingId: string }) => api.del(`/api/v1/hr/employees/${employeeId}/trainings/${trainingId}`))

// --- Bordro ---

export type PayrollPeriod = { rows: BordroSatir[]; generated: boolean; status: string; pendingOT: number }

export function usePayrollPeriod(period: string) {
  const people = usePersonelQuery()
  const q = useQuery({
    queryKey: hrKeys.payroll(period),
    enabled: !!period,
    queryFn: async () => {
      try {
        return rec(await api.get<unknown>(`/api/v1/hr/payroll/periods/${period}`))
      } catch (e) {
        // Dönem henüz hesaplanmadı: hata değil, boş dönem.
        if (isApiError(e) && e.status === 404) return null
        throw e
      }
    },
  })
  const data = useMemo<PayrollPeriod | undefined>(() => {
    if (q.data === undefined) return undefined
    if (q.data === null) return { rows: [], generated: false, status: '', pendingOT: 0 }
    return {
      rows: payrollRows(q.data, people.data ?? [], period),
      generated: true,
      status: str(q.data.status),
      pendingOT: Number(q.data.pending_ot_count) || 0,
    }
  }, [q.data, people.data, period])
  return { ...q, data }
}

export function useClosePayrollPeriod() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (period: string) => api.post(`/api/v1/hr/payroll/periods/${period}/close`, {}),
    onSuccess: (_d, period) => void qc.invalidateQueries({ queryKey: hrKeys.payroll(period) }),
  })
}

export function useSetBordroDurum() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ row, durum }: { row: BordroSatir; durum: BordroDurum }) =>
      api.patch(`/api/v1/hr/payroll/payrolls/${row.id}/status`, { status: bordroApiStatus(durum) }),
    onSuccess: (_d, { row }) => {
      if (row.donem) void qc.invalidateQueries({ queryKey: hrKeys.payroll(row.donem) })
      invalidateSection(qc, row.personel_id, 'payrolls')
    },
  })
}

// --- Ödemeler (tek toplu istek; personel başına istek yok) ---

export function usePayments() {
  const people = usePersonelQuery()
  const q = useQuery({ queryKey: hrKeys.payments, queryFn: async () => asRows(await api.get<unknown>('/api/v1/hr/payroll/payments')) })
  const data = useMemo<OdemeView[] | undefined>(() => q.data?.map((row) => mapPayment(row, people.data ?? [])), [q.data, people.data])
  return { ...q, data }
}

function invalidatePayments(qc: QueryClient, employeeIds: string[]) {
  void qc.invalidateQueries({ queryKey: hrKeys.payments })
  new Set(employeeIds).forEach((id) => invalidateSection(qc, id, 'payments'))
}

export function useApprovePayment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id }: { id: string; employeeId: string }) => api.post(`/api/v1/hr/payroll/payments/${id}/approve`, {}),
    onSuccess: (_d, { employeeId }) => invalidatePayments(qc, [employeeId]),
  })
}

export function useRejectPayment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; employeeId: string; reason?: string }) => api.post(`/api/v1/hr/payroll/payments/${id}/reject`, { reason: reason ?? '' }),
    onSuccess: (_d, { employeeId }) => invalidatePayments(qc, [employeeId]),
  })
}

/** Patron onaylı bekleyen ödemeleri "ödendi" işaretler (tekli ya da toplu tek istek). */
export function useMarkPaymentsPaid() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ rows, paidAt }: { rows: Pick<OdemeView, 'id' | 'personel_id'>[]; paidAt?: string }) =>
      api.post('/api/v1/hr/payroll/payments/mark-paid', { ids: rows.map((r) => r.id), paid_at: paidAt }),
    onSuccess: (_d, { rows }) => invalidatePayments(qc, rows.map((r) => r.personel_id)),
  })
}

export function useCreatePayment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { employeeId: string; amount: string; period: string; description: string }) =>
      api.post(`/api/v1/hr/payroll/employees/${input.employeeId}/payments/bonus`, {
        amount: money(input.amount),
        period: input.period,
        description: input.description,
      }),
    onSuccess: (_d, { employeeId }) => invalidatePayments(qc, [employeeId]),
  })
}

export function useCreateAllowance() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { employeeId: string; type: string; amount: string; validFrom: string }) =>
      api.post(`/api/v1/hr/payroll/employees/${input.employeeId}/allowances`, {
        allowance_type: input.type,
        amount: money(input.amount),
        currency_code: 'TRY',
        valid_from: input.validFrom,
      }),
    onSuccess: (_d, { employeeId }) => invalidateSection(qc, employeeId, 'allowances'),
  })
}

/** Banka talimat dosyası sunucuda üretilir (açık IBAN tarayıcıda tutulmaz). */
export function useDownloadBankFile() {
  return useMutation({
    mutationFn: (period?: string) => downloadFile('/api/v1/hr/payroll/payments/bank-file', `banka_dosyasi${period ? `_${period}` : ''}.csv`, { period }),
  })
}

export type { BordroSatir, OdemeView, Personel }

/* ---- Bordro parametreleri (asgari ücret, SGK/işsizlik oranları, teşvik, gelir vergisi dilimleri) ---- */

export type PayrollBracket = { upTo: string; rate: string }
export type PayrollParams = {
  id: string
  validFrom: string
  minWageGross: string
  sgkCeilingMultiplier: string
  employeeSgkRate: string
  employeeUnempRate: string
  employerSgkRate: string
  employerUnempRate: string
  employerDiscountRate: string
  stampTaxRate: string
  brackets: PayrollBracket[]
  minWageExemption: boolean
  note: string
  minWageNet: string
  minWageEmployerCost: string
}
export type PayrollPreview = Record<string, string>

const s = (v: unknown) => (v == null ? '' : String(v))
function mapParams(raw: unknown): PayrollParams {
  const r = rec(raw)
  return {
    id: s(r.id),
    validFrom: s(r.valid_from),
    minWageGross: s(r.min_wage_gross),
    sgkCeilingMultiplier: s(r.sgk_ceiling_multiplier),
    employeeSgkRate: s(r.employee_sgk_rate),
    employeeUnempRate: s(r.employee_unemp_rate),
    employerSgkRate: s(r.employer_sgk_rate),
    employerUnempRate: s(r.employer_unemp_rate),
    employerDiscountRate: s(r.employer_discount_rate),
    stampTaxRate: s(r.stamp_tax_rate),
    brackets: (Array.isArray(r.income_tax_brackets) ? r.income_tax_brackets : []).map((b) => ({ upTo: s(rec(b).up_to), rate: s(rec(b).rate) })),
    minWageExemption: r.min_wage_exemption !== false,
    note: s(r.note),
    minWageNet: s(r.min_wage_net),
    minWageEmployerCost: s(r.min_wage_employer_cost),
  }
}

/* ---- SGK teşvikleri: katalog (oran) ve personel teşvik dönemleri ---- */

export type Incentive = { id: string; code: string; name: string; rate: string; active: boolean; assigned: number }
export type EmployeeIncentive = { id: string; incentiveId: string; code: string; name: string; rate: string; start: string; end: string }
const incentiveKeys = { all: ['hr', 'incentives'] as const, employee: (id: string) => ['hr', 'incentives', 'employee', id] as const }

export function useIncentives(enabled = true) {
  return useQuery({
    queryKey: incentiveKeys.all,
    enabled,
    queryFn: async () =>
      asRows(await api.get<unknown>('/api/v1/hr/payroll/incentives')).map((raw): Incentive => {
        const r = rec(raw)
        return { id: s(r.id), code: s(r.code), name: s(r.name), rate: s(r.discount_percentage), active: r.is_active !== false, assigned: Number(r.assigned) || 0 }
      }),
  })
}

export function useSaveIncentive() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (i: Omit<Incentive, 'assigned'>) => {
      const body = { code: i.code.trim(), name: i.name.trim(), discount_percentage: i.rate || '0', is_active: i.active }
      return i.id ? api.put(`/api/v1/hr/payroll/incentives/${i.id}`, body) : api.post('/api/v1/hr/payroll/incentives', body)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: incentiveKeys.all }),
  })
}

export function useDeleteIncentive() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: (id: string) => api.del(`/api/v1/hr/payroll/incentives/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: incentiveKeys.all }) })
}

export function useEmployeeIncentives(employeeId: string, enabled = true) {
  return useQuery({
    queryKey: incentiveKeys.employee(employeeId),
    enabled: enabled && !!employeeId,
    queryFn: async () =>
      asRows(await api.get<unknown>(`/api/v1/hr/payroll/employees/${employeeId}/incentives`)).map((raw): EmployeeIncentive => {
        const r = rec(raw)
        return { id: s(r.id), incentiveId: s(r.incentive_id), code: s(r.code), name: s(r.name), rate: s(r.discount_percentage), start: s(r.start_date), end: s(r.end_date) }
      }),
  })
}

export function useAssignIncentive(employeeId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (a: { incentiveId: string; start: string; end: string }) =>
      api.post(`/api/v1/hr/payroll/employees/${employeeId}/incentives`, { incentive_id: a.incentiveId, start_date: a.start, end_date: a.end }),
    onSuccess: () => qc.invalidateQueries({ queryKey: incentiveKeys.all }),
  })
}

export function useUnassignIncentive() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (assignmentId: string) => api.del(`/api/v1/hr/payroll/incentives/assignments/${assignmentId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: incentiveKeys.all }),
  })
}

export function usePayrollParams(enabled = true) {
  return useQuery({ queryKey: hrKeys.payrollParams, enabled, queryFn: async () => asRows(await api.get<unknown>('/api/v1/hr/payroll/params')).map(mapParams) })
}

export function useSavePayrollParams() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (p: PayrollParams) => {
      const body = {
        valid_from: p.validFrom,
        min_wage_gross: p.minWageGross,
        sgk_ceiling_multiplier: p.sgkCeilingMultiplier,
        employee_sgk_rate: p.employeeSgkRate,
        employee_unemp_rate: p.employeeUnempRate,
        employer_sgk_rate: p.employerSgkRate,
        employer_unemp_rate: p.employerUnempRate,
        employer_discount_rate: p.employerDiscountRate,
        stamp_tax_rate: p.stampTaxRate,
        income_tax_brackets: p.brackets.map((b) => ({ up_to: b.upTo.trim() ? b.upTo : null, rate: b.rate })),
        min_wage_exemption: p.minWageExemption,
        note: p.note,
      }
      return p.id ? api.put(`/api/v1/hr/payroll/params/${p.id}`, body) : api.post('/api/v1/hr/payroll/params', body)
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: hrKeys.payrollParams }),
  })
}

export function useDeletePayrollParams() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.del(`/api/v1/hr/payroll/params/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: hrKeys.payrollParams }),
  })
}

/** Brütten nete önizleme: sunucu aynı motorla hesaplar (istemcide oran tekrarı yok). */
export function usePayrollPreview() {
  return useMutation({
    mutationFn: async (input: { gross: string; period: string; priorTaxBase: string }) => {
      const r = rec(await api.post<unknown>('/api/v1/hr/payroll/params/preview', { gross: input.gross, period: input.period, prior_tax_base: input.priorTaxBase || '0' }))
      return Object.fromEntries(Object.entries(r).map(([k, v]) => [k, s(v)])) as PayrollPreview
    },
  })
}
